import React, { useState, useEffect } from 'react';
import { BarChart2, Zap, Shield, TrendingUp, Star, Users, Activity, CheckCircle } from 'lucide-react';
import TickerSearchInput from './TickerSearchInput'; // TICKER-SEARCH-AUTOCOMPLETE — FIXED: HeroSection.jsx and TickerSearchInput.jsx are siblings in src/components/, not one level apart
import { s as sharedStyles } from '../styles/shared';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ─── Animated Counter ────────────────────────────────────────────────────────
const Counter = ({ target, prefix = '', suffix = '', duration = 1800 }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration]);
  return <span>{prefix}{count.toLocaleString()}{suffix}</span>;
};

// ─── How It Works Step ───────────────────────────────────────────────────────
const Step = ({ num, title, desc, icon, color }) => (
  <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'flex-start' }}>
    <div style={{ flexShrink: 0, width: 44, height: 44, borderRadius: '12px',
      background: `${color}12`, border: `1px solid ${color}25`,
      display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>
      {icon}
    </div>
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.3rem' }}>
        <span style={{ fontSize: '0.65rem', fontWeight: 900, color, letterSpacing: '0.1em' }}>0{num}</span>
        <h4 style={{ fontSize: '0.95rem', fontWeight: 800, color: '#f8fafc', margin: 0 }}>{title}</h4>
      </div>
      <p style={{ fontSize: '0.83rem', color: '#64748b', margin: 0, lineHeight: 1.65 }}>{desc}</p>
    </div>
  </div>
);

// ─── Live Signal Preview (guest hook — 4 signals, rest gated) ───────────────
// D3 NOTE: These pre-computed signals are the same Tier 1 signals D3 uses for
// its pre-market scan. Showing them here creates the member habit of checking
// signals daily — the exact behaviour D3 autonomous mode builds on.
const PREVIEW_TICKERS = ['AAPL', 'NVDA', 'MSFT', 'TSLA'];
const sigColor = s => s === 'BULLISH' ? '#00ff88' : s === 'BEARISH' ? '#ff4444' : '#ffaa00';
const sigIcon  = s => s === 'BULLISH' ? '▲' : s === 'BEARISH' ? '▼' : '◆';

function SignalPreviewPanel({ user, onNavigate, setShowInvite, setShowAuth, setAuthMode }) {
  const [signals, setSignals] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all(PREVIEW_TICKERS.map(async t => {
      try {
        const res  = await fetch(`${API_URL}/api/seo/stocks/${t}`);
        const data = await res.json();
        if (!cancelled && data.success) {
          setSignals(prev => ({ ...prev, [t]: {
            signal: data.signal, score: data.score,
            price: data.quote?.price || data.quote?.prevClose,
          }}));
        }
      } catch (e) {}
    })).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={{ background: 'rgba(10,20,45,0.7)', border: '1px solid rgba(0,212,255,0.12)',
      borderRadius: 16, padding: '1.25rem', height: '100%', boxSizing: 'border-box' }}>

      <div style={{ fontSize: '0.68rem', fontWeight: 800, color: '#64748b',
        letterSpacing: '0.1em', marginBottom: '0.25rem' }}>
        SIGNAL PREVIEW · Price Action
      </div>
      <div style={{ fontSize: '0.6rem', color: '#334155', marginBottom: '0.75rem', letterSpacing: '0.03em' }}>
        Based on today's price movement · Run full analysis for complete score
      </div>

      {/* 4 visible signals */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: '0.85rem' }}>
        {PREVIEW_TICKERS.map(t => {
          const s = signals[t];
          return (
            <div key={t} onClick={() => onNavigate('stock', { ticker: t })}
              style={{ display: 'flex', alignItems: 'center', gap: '0.65rem',
                padding: '0.55rem 0.75rem', borderRadius: 9, cursor: 'pointer',
                background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)',
                transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,212,255,0.05)'; e.currentTarget.style.borderColor = 'rgba(0,212,255,0.2)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.05)'; }}>
              <span style={{ fontWeight: 900, fontSize: '0.88rem', color: '#f8fafc', minWidth: 52 }}>{t}</span>
              {loading || !s ? (
                <span style={{ fontSize: '0.7rem', color: '#334155' }}>Loading…</span>
              ) : (
                <>
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, color: sigColor(s.signal) }}>
                    {sigIcon(s.signal)} {s.signal}
                  </span>
                  <span style={{ fontSize: '0.68rem', color: '#475569', marginLeft: 'auto' }}>
                    {s.score}/100
                  </span>
                  {s.price && (
                    <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                      ${Number(s.price).toFixed(2)}
                    </span>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Gate — rest of signals locked */}
      <div style={{ padding: '0.75rem', background: 'rgba(0,255,136,0.04)',
        border: '1px solid rgba(0,255,136,0.12)', borderRadius: 10, textAlign: 'center' }}>
        {user ? (
          <>
            <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: 8 }}>
              View all signals + your watchlist
            </div>
            <button onClick={() => onNavigate('dashboard')}
              style={{ width: '100%', padding: '0.55rem', background: 'rgba(0,255,136,0.1)',
                border: '1px solid rgba(0,255,136,0.25)', borderRadius: 8,
                color: '#00ff88', fontSize: '0.78rem', fontWeight: 800,
                cursor: 'pointer', fontFamily: 'inherit' }}>
              My Dashboard →
            </button>
          </>
        ) : (
          <>
            <div style={{ fontSize: '0.7rem', color: '#475569', marginBottom: 8 }}>
              <span style={{ color: '#00ff88', fontWeight: 700 }}>229 more signals</span>
              {' '}available for members
            </div>
            <button onClick={() => setShowInvite(true)}
              style={{ width: '100%', padding: '0.55rem', background: 'linear-gradient(135deg,rgba(0,255,136,0.15),rgba(0,212,255,0.1))',
                border: '1px solid rgba(0,255,136,0.3)', borderRadius: 8,
                color: '#00ff88', fontSize: '0.78rem', fontWeight: 800,
                cursor: 'pointer', fontFamily: 'inherit' }}>
              Request Invite →
            </button>
            <button onClick={() => { setAuthMode('login'); setShowAuth(true); }}
              style={{ width: '100%', marginTop: 6, padding: '0.45rem',
                background: 'transparent', border: 'none',
                color: '#475569', fontSize: '0.72rem', cursor: 'pointer',
                fontFamily: 'inherit' }}>
              Already a member? Sign in
            </button>
          </>
        )}
      </div>

      <div style={{ marginTop: 8, fontSize: '0.62rem', color: '#1e293b', textAlign: 'center' }}>
        Algorithmic signals · Not investment advice · 15-min delayed data
      </div>
    </div>
  );
}

// ─── Main HeroSection ────────────────────────────────────────────────────────
const HeroSection = ({
  ticker, setTicker, onAnalyze, loading, error, user,
  setShowAuth, setAuthMode, setPage, setShowInvite, marketData, onNavigate,
}) => {
  // TICKER-SEARCH-AUTOCOMPLETE: inputFocused/setInputFocused removed — was only
  // used by the old plain <input>'s onFocus/onBlur + border styling, both replaced
  // by TickerSearchInput, which manages its own internal focus/dropdown state.
  const exampleTickers = ['AAPL', 'TSLA', 'NVDA', 'MSFT', 'GOOGL', 'AMZN', 'META'];

  return (
    <div style={{ fontFamily: '"DM Sans", system-ui, sans-serif' }}>

      {/* ── Hero — two column: input left, signal preview right ── */}
      <div style={{ position: 'relative', overflow: 'hidden', padding: '4rem 0 2.5rem' }}>

        {/* Background grid */}
        <div style={{ position: 'absolute', inset: 0,
          backgroundImage: 'linear-gradient(rgba(0,212,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,255,0.03) 1px, transparent 1px)',
          backgroundSize: '60px 60px', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: '-100px', left: '10%', width: '500px', height: '500px',
          background: 'radial-gradient(circle, rgba(0,255,136,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: 0, right: '5%', width: '400px', height: '400px',
          background: 'radial-gradient(circle, rgba(0,212,255,0.05) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <div style={{ position: 'relative', maxWidth: sharedStyles.container.maxWidth, margin: '0 auto', padding: '0 1.5rem' }}>

          {/* Two-column layout */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr minmax(0,320px)', gap: '2.5rem', alignItems: 'center' }}>

            {/* LEFT — Hero input */}
            <div>
              {/* Badge */}
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.35rem 1rem', background: 'rgba(0,255,136,0.08)',
                border: '1px solid rgba(0,255,136,0.2)', borderRadius: '20px', marginBottom: '1.5rem' }}>
                <Activity size={12} style={{ color: '#00ff88' }} />
                <span style={{ fontSize: '0.72rem', color: '#00ff88', fontWeight: 700, letterSpacing: '0.06em' }}>
                  ALGORITHMIC · 15-MIN DELAYED DATA · US EQUITIES · INVITE-ONLY BETA
                </span>
              </div>

              {/* Headline */}
              <h1 style={{ fontSize: 'clamp(2.2rem, 5vw, 3.5rem)', fontWeight: 900, color: '#f8fafc',
                margin: '0 0 1rem', letterSpacing: '-0.04em', lineHeight: 1.1 }}>
                Analyze any stock<br />
                <span style={{ background: 'linear-gradient(135deg,#00ff88,#00d4ff)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  in seconds.
                </span>
              </h1>

              <p style={{ fontSize: 'clamp(0.9rem, 2vw, 1.05rem)', color: '#94a3b8',
                margin: '0 0 2rem', maxWidth: 520, lineHeight: 1.7 }}>
                Algorithmic BULLISH / BEARISH / NEUTRAL signals, quantitative scoring, and market intelligence.
                Built for traders who need signal, not noise.
              </p>

              {/* Search box — TICKER-SEARCH-AUTOCOMPLETE session: swapped plain input for
                  the shared tiered dropdown. onChange keeps this component's own `ticker`
                  state in sync (the Analyze Now button below calls onAnalyze() with no
                  args, reading that state directly) — onSubmit preserves the original
                  "Enter always analyzes whatever was typed" behavior, independent of
                  whether a dropdown suggestion loaded yet. onSelect (clicking a real
                  suggestion) goes one step further and analyzes immediately, same
                  one-click philosophy as the "Quick tickers" buttons below. */}
              <div style={{ display: 'flex', gap: '0.65rem', maxWidth: 520, marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                <TickerSearchInput
                  initialValue={ticker}
                  placeholder="Enter ticker (AAPL, TSLA, NVDA…)"
                  onChange={(v) => setTicker(v)}
                  onSubmit={(v) => onAnalyze(v)}
                  onSelect={(t) => { setTicker(t); onAnalyze(t); }}
                  style={{ flex: '1 1 240px' }}
                />
                <button onClick={onAnalyze} disabled={loading}
                  style={{ padding: '0.9rem 1.75rem', background: 'linear-gradient(135deg,#00ff88,#00d4ff)',
                    border: 'none', borderRadius: '12px', color: '#050d1a', fontWeight: 900,
                    fontSize: '0.95rem', cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: '0.5rem',
                    whiteSpace: 'nowrap', fontFamily: 'inherit',
                    boxShadow: '0 4px 20px rgba(0,255,136,0.25)' }}>
                  {loading ? '⏳ Analyzing…' : <><Zap size={15} /> Analyze Now</>}
                </button>
              </div>

              {/* ── FIX: Error message display ── */}
              {/* Previously: error prop was accepted but never rendered — user saw nothing */}
              {error && !error.startsWith('SESSION_EXPIRED:') && (
                <div style={{
                  maxWidth: 520, marginBottom: '0.75rem',
                  padding: '0.6rem 1rem',
                  background: 'rgba(239,68,68,0.08)',
                  border: '1px solid rgba(239,68,68,0.25)',
                  borderRadius: '8px',
                  fontSize: '0.82rem', color: '#ef4444', fontWeight: 500,
                }}>
                  {error.includes(':') ? error.split(':').slice(1).join(':').trim() : error}
                </div>
              )}
              {error && error.startsWith('SESSION_EXPIRED:') && (
                <div style={{
                  maxWidth: 520, marginBottom: '0.75rem',
                  padding: '0.6rem 1rem',
                  background: 'rgba(245,158,11,0.08)',
                  border: '1px solid rgba(245,158,11,0.25)',
                  borderRadius: '8px',
                  fontSize: '0.82rem', color: '#f59e0b', fontWeight: 500,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem',
                }}>
                  <span>Session expired.</span>
                  <button
                    onClick={() => { setAuthMode('login'); setShowAuth(true); }}
                    style={{ padding: '0.25rem 0.75rem', background: 'rgba(245,158,11,0.15)',
                      border: '1px solid rgba(245,158,11,0.4)', borderRadius: '6px',
                      color: '#f59e0b', fontSize: '0.78rem', fontWeight: 700,
                      cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                    Sign in again →
                  </button>
                </div>
              )}

              {/* Quick tickers */}
              {/* FIX: now calls onAnalyze(t) directly — one click to analyze, not two */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
                <span style={{ fontSize: '0.72rem', color: '#334155' }}>Try:</span>
                {exampleTickers.map(t => (
                  <button key={t} onClick={() => { setTicker(t); onAnalyze(t); }}
                    style={{ padding: '0.22rem 0.6rem', background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.08)', borderRadius: '6px',
                      color: '#64748b', fontSize: '0.72rem', fontWeight: 700,
                      cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '0.04em', transition: 'all 0.15s' }}
                    onMouseEnter={e => { e.target.style.color = '#00d4ff'; e.target.style.borderColor = 'rgba(0,212,255,0.3)'; }}
                    onMouseLeave={e => { e.target.style.color = '#64748b'; e.target.style.borderColor = 'rgba(255,255,255,0.08)'; }}>
                    {t}
                  </button>
                ))}
              </div>

              {/* Member Dashboard CTA */}
              {user && (
                <div style={{ marginTop: '0.5rem' }}>
                  <button onClick={() => onNavigate('dashboard')}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                      padding: '0.55rem 1.1rem', background: 'rgba(0,255,136,0.07)',
                      border: '1px solid rgba(0,255,136,0.2)', borderRadius: '10px',
                      color: '#00ff88', fontSize: '0.82rem', fontWeight: 700,
                      cursor: 'pointer', fontFamily: 'inherit' }}>
                    ★ My Dashboard → <span style={{ color: '#475569', fontWeight: 400 }}>Watchlist + signals</span>
                  </button>
                </div>
              )}
            </div>

            {/* RIGHT — Signal preview panel (desktop only) */}
            <div className="signal-panel-desktop">
              <SignalPreviewPanel
                user={user}
                onNavigate={onNavigate}
                setShowInvite={setShowInvite}
                setShowAuth={setShowAuth}
                setAuthMode={setAuthMode}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Market data strip ── */}
      {marketData && (
        <div style={{ background: 'rgba(5,13,26,0.8)', borderTop: '1px solid rgba(0,212,255,0.07)',
          borderBottom: '1px solid rgba(0,212,255,0.07)', padding: '0.9rem 0' }}>
          <div style={{ maxWidth: sharedStyles.container.maxWidth, margin: '0 auto', padding: '0 1.5rem',
            display: 'flex', gap: '1.5rem', overflowX: 'auto', alignItems: 'center' }}>
            {[
              { label: 'S&P 500',  val: marketData.sp500?.price,  chg: marketData.sp500?.changePercent  },
              { label: 'DOW',      val: marketData.dow?.price,    chg: marketData.dow?.changePercent    },
              { label: 'NASDAQ',   val: marketData.nasdaq?.price, chg: marketData.nasdaq?.changePercent },
              { label: 'RUSSELL',  val: marketData.russell?.price,chg: marketData.russell?.changePercent},
            ].filter(i => i.val).map(({ label, val, chg }) => (
              <div key={label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                minWidth: 80, flexShrink: 0 }}>
                <span style={{ fontSize: '0.65rem', color: '#334155', fontWeight: 700,
                  letterSpacing: '0.08em', marginBottom: 2 }}>{label}</span>
                <span style={{ fontSize: '0.95rem', fontWeight: 800, color: '#f8fafc' }}>
                  {Number(val).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </span>
                {chg != null && (
                  <span style={{ fontSize: '0.7rem', fontWeight: 600,
                    color: chg >= 0 ? '#00ff88' : '#ef4444' }}>
                    {chg >= 0 ? '+' : ''}{Number(chg).toFixed(2)}%
                  </span>
                )}
              </div>
            ))}
            <button onClick={() => onNavigate('market')}
              style={{ marginLeft: 'auto', flexShrink: 0, background: 'none', border: 'none',
                color: '#00d4ff', fontSize: '0.78rem', cursor: 'pointer', fontWeight: 600,
                fontFamily: 'inherit', padding: 0 }}>
              View full market overview →
            </button>
          </div>
        </div>
      )}

      {/* ── Social Proof Stats ── */}
      <div style={{ borderTop: '1px solid rgba(0,212,255,0.07)', borderBottom: '1px solid rgba(0,212,255,0.07)',
        background: 'rgba(5,13,26,0.6)', padding: '1.75rem 0' }}>
        <div style={{ maxWidth: sharedStyles.container.maxWidth, margin: '0 auto', padding: '0 1.5rem',
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: '1.25rem', textAlign: 'center' }}>
          {[
            { value: 500,  suffix: '+',    label: 'US Equities',        icon: <BarChart2 size={16} />, color: '#00d4ff' },
            { value: 50,   suffix: '+',    label: 'Analyses Run',       icon: <Activity  size={16} />, color: '#00ff88' },
            { value: 100,  suffix: '%',    label: 'Invite-Only Access', icon: <Star      size={16} />, color: '#ffaa00' },
            { value: 15,   suffix: '-min', label: 'Data Delay',         icon: <Activity  size={16} />, color: '#ffaa00' },
          ].map((s, i) => (
            <div key={i}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.4rem', color: s.color, opacity: 0.7 }}>{s.icon}</div>
              <div style={{ fontSize: '1.75rem', fontWeight: 900, color: '#f8fafc', letterSpacing: '-0.03em', lineHeight: 1 }}>
                <Counter target={s.value} suffix={s.suffix} />
              </div>
              <div style={{ fontSize: '0.72rem', color: '#475569', marginTop: '0.3rem', fontWeight: 600 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── How It Works — compact 4 steps ── */}
      <div style={{ maxWidth: sharedStyles.container.maxWidth, margin: '0 auto', padding: '4rem 1.5rem 2.5rem' }}>
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <div style={{ display: 'inline-block', background: 'rgba(0,212,255,0.08)',
            border: '1px solid rgba(0,212,255,0.2)', borderRadius: '20px',
            padding: '0.3rem 1rem', marginBottom: '0.85rem' }}>
            <span style={{ fontSize: '0.72rem', color: '#00d4ff', fontWeight: 700, letterSpacing: '0.1em' }}>HOW IT WORKS</span>
          </div>
          <h2 style={{ fontSize: 'clamp(1.6rem, 3.5vw, 2.25rem)', fontWeight: 900, color: '#f8fafc',
            margin: 0, letterSpacing: '-0.03em' }}>
            Institutional analysis. In under 30 seconds.
          </h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.25rem' }}>
          {[
            { num: 1, title: 'Enter a ticker',      color: '#00d4ff', icon: <BarChart2 size={20} />,
              desc: 'Any US equity ticker — AAPL, TSLA, NVDA and thousands more.' },
            { num: 2, title: 'Data is fetched',      color: '#00ff88', icon: <Activity  size={20} />,
              desc: 'Delayed price data, fundamentals, earnings, and market data from licensed sources.' },
            { num: 3, title: 'Score is calculated',  color: '#ffaa00', icon: <Star      size={20} />,
              desc: 'Quantitative model scores the stock 0–100 across momentum, fundamentals, valuation, and risk.' },
            { num: 4, title: 'Get your signal',      color: '#00ff88', icon: <Zap       size={20} />,
              desc: 'Clear BULLISH / BEARISH / NEUTRAL signal with key reasons, risks, and a model estimate.' },
          ].map(s => (
            <div key={s.num} style={{ background: 'rgba(10,20,45,0.5)',
              border: '1px solid rgba(255,255,255,0.05)', borderRadius: '14px', padding: '1.5rem' }}>
              <Step {...s} />
            </div>
          ))}
        </div>
      </div>

      {/* ── Feature Highlights ── */}
      <div style={{ maxWidth: sharedStyles.container.maxWidth, margin: '0 auto', padding: '0 1.5rem 4rem' }}>
        <h2 style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', fontWeight: 900, color: '#f8fafc',
          margin: '0 0 1.75rem', letterSpacing: '-0.03em', textAlign: 'center' }}>
          Everything you need to trade with more conviction
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0.85rem' }}>
          {[
            { icon: <BarChart2 size={18} />, color: '#00d4ff', title: 'Delayed Market Data',
              desc: 'Prices, volume, indices — 15-min delayed data from licensed sources.' },
            { icon: <Zap size={18} />,        color: '#00ff88', title: 'Algorithmic Signals',
              desc: 'BULLISH / BEARISH / NEUTRAL backed by quantitative scoring.' },
            { icon: <Shield size={18} />,     color: '#ffaa00', title: 'Risk Analysis',
              desc: 'Key risk factors highlighted so you know what could go wrong.' },
            { icon: <TrendingUp size={18} />, color: '#00ff88', title: 'Model Estimates',
              desc: 'Model-based price estimates with upside/downside percentages.' },
            { icon: <Users size={18} />,      color: '#00d4ff', title: 'Analysis History',
              desc: 'Every analysis you run is saved and searchable in your profile.' },
            { icon: <Activity size={18} />,   color: '#ff4444', title: 'Market Intelligence',
              desc: 'Fear & Greed, VIX, yields, sector flow — daily updated.' },
          ].map((f, i) => (
            <div key={i} style={{ display: 'flex', gap: '0.9rem', alignItems: 'flex-start',
              padding: '1.25rem', background: 'rgba(10,20,45,0.4)',
              border: '1px solid rgba(255,255,255,0.04)', borderRadius: '12px' }}>
              <div style={{ width: 36, height: 36, borderRadius: '10px',
                background: `${f.color}12`, border: `1px solid ${f.color}20`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: f.color, flexShrink: 0 }}>{f.icon}</div>
              <div>
                <h4 style={{ color: '#f8fafc', fontWeight: 700, fontSize: '0.88rem', margin: '0 0 0.3rem' }}>{f.title}</h4>
                <p style={{ color: '#475569', fontSize: '0.8rem', margin: 0, lineHeight: 1.6 }}>{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Guest CTA Banner ── */}
      {!user && (
        <div style={{ background: 'linear-gradient(135deg, rgba(0,255,136,0.07), rgba(0,212,255,0.05))',
          border: '1px solid rgba(0,255,136,0.12)', borderRadius: '20px',
          padding: '3rem 2rem', margin: '0 1.5rem 5rem',
          maxWidth: 900, marginLeft: 'auto', marginRight: 'auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(1.4rem, 3vw, 2rem)', fontWeight: 900, color: '#f8fafc',
            margin: '0 0 0.75rem', letterSpacing: '-0.03em' }}>
            Algorithmic stock analysis — invite only
          </h2>
          <p style={{ color: '#64748b', fontSize: '0.95rem', margin: '0 0 1.75rem' }}>
            Invite-only beta. Guest preview available to everyone. Full analysis for invited members.
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => setShowInvite(true)}
              style={{ padding: '0.85rem 2.25rem', background: 'linear-gradient(135deg,#00ff88,#00d4ff)',
                border: 'none', borderRadius: '12px', color: '#050d1a', fontWeight: 900,
                fontSize: '0.95rem', cursor: 'pointer', fontFamily: 'inherit',
                boxShadow: '0 4px 24px rgba(0,255,136,0.2)' }}>
              Request Invite →
            </button>
            <button onClick={() => { setAuthMode('login'); setShowAuth(true); }}
              style={{ padding: '0.85rem 1.75rem', background: 'transparent',
                border: '1px solid rgba(255,255,255,0.12)', borderRadius: '12px',
                color: '#94a3b8', fontWeight: 600, fontSize: '0.95rem',
                cursor: 'pointer', fontFamily: 'inherit' }}>
              Sign In
            </button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', marginTop: '1.25rem', flexWrap: 'wrap' }}>
            {['Guest preview — no login', 'Full analysis — invite only', '15-min delayed data'].map(f => (
              <div key={f} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem',
                fontSize: '0.75rem', color: '#475569' }}>
                <CheckCircle size={11} style={{ color: '#00ff88' }} /> {f}
              </div>
            ))}
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .signal-panel-desktop { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default HeroSection;
